import moment from 'moment';
import {
  denormalisedEntities,
  denormalisedResponseEntities,
  ensureAvailabilityException,
  updatedEntities,
} from '../../util/data';
import { isSameDate, monthIdStringInUTC } from '../../util/dates';
import { storableError } from '../../util/errors';
import { logError } from '../../util/log';
import { parse } from '../../util/urlHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 12;

// A helper function to filter away exception that matches start and end timestamps
const removeException = (exception, calendar) => {
  const availabilityException = ensureAvailabilityException(exception.availabilityException);
  const { start, end } = availabilityException.attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);
  const monthData = calendar[monthId] || { exceptions: [] };

  const exceptions = monthData.exceptions.filter((e) => {
    const anException = ensureAvailabilityException(e.availabilityException);
    const exceptionStart = anException.attributes.start;
    const exceptionEnd = anException.attributes.end;

    return !(isSameDate(exceptionStart, start) && isSameDate(exceptionEnd, end));
  });

  return {
    ...calendar,
    [monthId]: { ...monthData, exceptions },
  };
};

// A helper function to add a new exception and remove previous one if there's a matching exception
const addException = (exception, calendar) => {
  const { start } = ensureAvailabilityException(exception.availabilityException).attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);

  // TODO: API doesn't support "availability_exceptions/update" yet
  // So, when user wants to create an exception we need to ensure
  // that possible existing exception is removed first.
  const cleanCalendar = removeException(exception, calendar);
  const monthData = cleanCalendar[monthId] || { exceptions: [] };

  return {
    ...cleanCalendar,
    [monthId]: { ...monthData, exceptions: [...monthData.exceptions, exception] },
  };
};

// A helper function to update exception that matches start and end timestamps
const updateException = (exception, calendar) => {
  const newAvailabilityException = ensureAvailabilityException(exception.availabilityException);
  const { start, end } = newAvailabilityException.attributes;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);
  const monthData = calendar[monthId] || { exceptions: [] };

  const exceptions = monthData.exceptions.map((e) => {
    const availabilityException = ensureAvailabilityException(e.availabilityException);
    const exceptionStart = availabilityException.attributes.start;
    const exceptionEnd = availabilityException.attributes.end;

    return isSameDate(exceptionStart, start) && isSameDate(exceptionEnd, end) ? exception : e;
  });

  return {
    ...calendar,
    [monthId]: { ...monthData, exceptions },
  };
};

// Update calendar data of given month
const updateCalendarMonth = (state, monthId, data) => {
  // Ensure that every month has array for bookings and exceptions
  const defaultMonthData = { bookings: [], exceptions: [] };
  return {
    ...state,
    availabilityCalendar: {
      ...state.availabilityCalendar,
      [monthId]: {
        ...defaultMonthData,
        ...state.availabilityCalendar[monthId],
        ...data,
      },
    },
  };
};

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const CLEAR_AVAILABILITY_CALENDAR = 'app/ManageListingsPage/CLEAR_AVAILABILITY_CALENDAR';

export const FETCH_BOOKINGS_REQUEST = 'app/ManageListingsPage/FETCH_BOOKINGS_REQUEST';
export const FETCH_BOOKINGS_SUCCESS = 'app/ManageListingsPage/FETCH_BOOKINGS_SUCCESS';
export const FETCH_BOOKINGS_ERROR = 'app/ManageListingsPage/FETCH_BOOKINGS_ERROR';

export const FETCH_EXCEPTIONS_REQUEST =
  'app/ManageListingsPage/FETCH_AVAILABILITY_EXCEPTIONS_REQUEST';
export const FETCH_EXCEPTIONS_SUCCESS =
  'app/ManageListingsPage/FETCH_AVAILABILITY_EXCEPTIONS_SUCCESS';
export const FETCH_EXCEPTIONS_ERROR = 'app/ManageListingsPage/FETCH_AVAILABILITY_EXCEPTIONS_ERROR';

export const CREATE_EXCEPTION_REQUEST =
  'app/ManageListingsPage/CREATE_AVAILABILITY_EXCEPTION_REQUEST';
export const CREATE_EXCEPTION_SUCCESS =
  'app/ManageListingsPage/CREATE_AVAILABILITY_EXCEPTION_SUCCESS';
export const CREATE_EXCEPTION_ERROR = 'app/ManageListingsPage/CREATE_AVAILABILITY_EXCEPTION_ERROR';

export const DELETE_EXCEPTION_REQUEST =
  'app/ManageListingsPage/DELETE_AVAILABILITY_EXCEPTION_REQUEST';
export const DELETE_EXCEPTION_SUCCESS =
  'app/ManageListingsPage/DELETE_AVAILABILITY_EXCEPTION_SUCCESS';
export const DELETE_EXCEPTION_ERROR = 'app/ManageListingsPage/DELETE_AVAILABILITY_EXCEPTION_ERROR';

export const UPDATE_OWN_LISTING_REQUEST = 'app/ManageListingsPage/UPDATE_OWN_LISTING_REQUEST';
export const UPDATE_OWN_LISTING_SUCCESS = 'app/ManageListingsPage/UPDATE_OWN_LISTING_SUCCESS';
export const UPDATE_OWN_LISTING_ERROR = 'app/ManageListingsPage/UPDATE_OWN_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.ManageListingsPage} */
const initialState = {
  availabilityCalendar: {
    // '2018-12': {
    //   bookings: [],
    //   exceptions: [],
    //   fetchExceptionsError: null,
    //   fetchExceptionsInProgress: false,
    //   fetchBookingsError: null,
    //   fetchBookingsInProgress: false,
    // },
  },
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  updateListing: null,
  updateError: null,
};

const resultIds = (data) => data.data.map((l) => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const requestAction = (actionType) => (params) => ({ type: actionType, payload: { params } });

const successAction = (actionType) => (result) => ({ type: actionType, payload: result.data });

const errorAction = (actionType) => (error) => ({ type: actionType, payload: error, error: true });

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case CLEAR_AVAILABILITY_CALENDAR: {
      return { ...state, availabilityCalendar: {} };
    }

    case FETCH_BOOKINGS_REQUEST:
      return updateCalendarMonth(state, payload.params.monthId, {
        fetchBookingsError: null,
        fetchBookingsInProgress: true,
      });
    case FETCH_BOOKINGS_SUCCESS:
      return updateCalendarMonth(state, payload.monthId, {
        bookings: payload.bookings,
        fetchBookingsInProgress: false,
      });
    case FETCH_BOOKINGS_ERROR:
      return updateCalendarMonth(state, payload.monthId, {
        fetchBookingsError: payload.error,
        fetchBookingsInProgress: false,
      });

    case FETCH_EXCEPTIONS_REQUEST:
      return updateCalendarMonth(state, payload.params.monthId, {
        fetchExceptionsError: null,
        fetchExceptionsInProgress: true,
      });
    case FETCH_EXCEPTIONS_SUCCESS:
      return updateCalendarMonth(state, payload.monthId, {
        exceptions: payload.exceptions,
        fetchExceptionsInProgress: false,
      });
    case FETCH_EXCEPTIONS_ERROR:
      return updateCalendarMonth(state, payload.monthId, {
        fetchExceptionsError: payload.error,
        fetchExceptionsInProgress: false,
      });

    case CREATE_EXCEPTION_REQUEST: {
      const { start, end, seats } = payload.params;
      const draft = ensureAvailabilityException({ attributes: { start, end, seats } });
      const exception = { availabilityException: draft, inProgress: true };
      const availabilityCalendar = addException(exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case CREATE_EXCEPTION_SUCCESS: {
      const availabilityCalendar = updateException(payload.exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case CREATE_EXCEPTION_ERROR: {
      const { availabilityException, error } = payload;
      const failedException = { availabilityException, error };
      const availabilityCalendar = updateException(failedException, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }

    case DELETE_EXCEPTION_REQUEST: {
      const { id, seats, currentException } = payload.params;

      // We first create temporary exception with given 'seats' count (the default after deletion).
      // This makes it possible to show the UI element immediately with default color that matches
      // with the availability plan.
      const exception = {
        id,
        inProgress: true,
        availabilityException: {
          ...currentException.availabilityException,
          attributes: { ...currentException.availabilityException.attributes, seats },
        },
      };

      const availabilityCalendar = updateException(exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case DELETE_EXCEPTION_SUCCESS: {
      const availabilityCalendar = removeException(payload.exception, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }
    case DELETE_EXCEPTION_ERROR: {
      const { availabilityException, error } = payload;
      const failedException = { availabilityException, error };
      const availabilityCalendar = updateException(failedException, state.availabilityCalendar);
      return { ...state, availabilityCalendar };
    }

    case UPDATE_OWN_LISTING_REQUEST:
      return { ...state, updateListing: null, updateError: null };
    case UPDATE_OWN_LISTING_SUCCESS:
      return { ...state, updateListing: payload.id };
    case UPDATE_OWN_LISTING_ERROR:
      return { ...state, updateListing: null, updateError: payload };

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {object} state the full Redux store
 * @param {Array<Sharetribe.UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map((id) => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = (sdkResponse) => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = (listingId) => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = (response) => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = (e) => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = (listingId) => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = (response) => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = (e) => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = (queryParams) => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = (response) => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = (e) => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// SDK method: bookings.query
export const fetchBookingsRequest = requestAction(FETCH_BOOKINGS_REQUEST);
export const fetchBookingsSuccess = successAction(FETCH_BOOKINGS_SUCCESS);
export const fetchBookingsError = errorAction(FETCH_BOOKINGS_ERROR);

// SDK method: availabilityExceptions.query
export const fetchAvailabilityExceptionsRequest = requestAction(FETCH_EXCEPTIONS_REQUEST);
export const fetchAvailabilityExceptionsSuccess = successAction(FETCH_EXCEPTIONS_SUCCESS);
export const fetchAvailabilityExceptionsError = errorAction(FETCH_EXCEPTIONS_ERROR);

// SDK method: availabilityExceptions.create
export const createAvailabilityExceptionRequest = requestAction(CREATE_EXCEPTION_REQUEST);
export const createAvailabilityExceptionSuccess = successAction(CREATE_EXCEPTION_SUCCESS);
export const createAvailabilityExceptionError = errorAction(CREATE_EXCEPTION_ERROR);

// SDK method: availabilityExceptions.delete
export const deleteAvailabilityExceptionRequest = requestAction(DELETE_EXCEPTION_REQUEST);
export const deleteAvailabilityExceptionSuccess = successAction(DELETE_EXCEPTION_SUCCESS);
export const deleteAvailabilityExceptionError = errorAction(DELETE_EXCEPTION_ERROR);

// SDK method: ownListings.update
export const updateOwnListingRequest = requestAction(UPDATE_OWN_LISTING_REQUEST);
export const updateOwnListingSuccess = successAction(UPDATE_OWN_LISTING_SUCCESS);
export const updateOwnListingError = errorAction(UPDATE_OWN_LISTING_ERROR);

export const clearAvailabilityCalendarRequest = () => ({ type: CLEAR_AVAILABILITY_CALENDAR });

// Throwing error for new (loadData may need that info)
export const queryOwnListings = (queryParams) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, per_page: perPage };

  return sdk.ownListings
    .query(params)
    .then((response) => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch((e) => {
      logError(e, 'query-own-listings', { queryParams });
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = (listingId) => async (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  try {
    const response = await sdk.ownListings.close({ id: listingId }, { expand: true });
    dispatch(closeListingSuccess(response));
    return response;
  } catch (e) {
    logError(e, 'close-listing', { listingId });
    dispatch(closeListingError(storableError(e)));
  }
};

export const openListing = (listingId) => async (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  try {
    const response = await sdk.ownListings.open({ id: listingId }, { expand: true });
    await dispatch(openListingSuccess(response));
    return response;
  } catch (e) {
    logError(e, 'open-listing', { listingId });
    dispatch(openListingError(storableError(e)));
  }
};

export const requestFetchBookings = (fetchParams) => async (dispatch, getState, sdk) => {
  const { listingId, start, end, state } = fetchParams;
  // When using time-based process, you might want to deal with local dates using monthIdString
  const monthId = monthIdStringInUTC(start);

  dispatch(fetchBookingsRequest({ ...fetchParams, monthId }));

  const startMonthId = monthIdStringInUTC(moment(start).clone().subtract(1, 'month').toDate());
  const startMonthStart = moment(start).clone().subtract(1, 'month').startOf('month').toISOString();
  const startMonthEnd = moment(start).clone().subtract(1, 'month').endOf('month').toISOString();
  const middleMonthId = monthIdStringInUTC(moment(start).clone().toDate());
  const middleMonthStart = moment(start).clone().startOf('month').toISOString();
  const middleMonthEnd = moment(start).clone().endOf('month').toISOString();
  const endMonthId = monthIdStringInUTC(moment(start).clone().add(1, 'month').toDate());
  const endMonthStart = moment(start).clone().add(1, 'month').startOf('month').toISOString();
  const endMonthEnd = moment(start).clone().add(1, 'month').endOf('month').toISOString();

  try {
    const [startMonth, middleMonth, endMonth] = await Promise.all([
      sdk.bookings.query(
        {
          listingId,
          start: startMonthStart,
          end: startMonthEnd,
          state,
          include: ['transaction.customer'],
        },
        { expand: true }
      ),
      sdk.bookings.query(
        {
          listingId,
          start: middleMonthStart,
          end: middleMonthEnd,
          state,
          include: ['transaction.customer'],
        },
        { expand: true }
      ),
      sdk.bookings.query(
        {
          listingId,
          start: endMonthStart,
          end: endMonthEnd,
          state,
          include: ['transaction.customer'],
        },
        { expand: true }
      ),
    ]);

    let bookings = denormalisedResponseEntities(startMonth);
    dispatch(fetchBookingsSuccess({ data: { monthId: startMonthId, bookings } }));
    bookings = denormalisedResponseEntities(middleMonth);
    dispatch(fetchBookingsSuccess({ data: { monthId: middleMonthId, bookings } }));
    bookings = denormalisedResponseEntities(endMonth);
    dispatch(fetchBookingsSuccess({ data: { monthId: endMonthId, bookings } }));
  } catch (e) {
    logError(e, 'fetch-bookings', { listingId, start, end, state });
    return dispatch(fetchBookingsError({ monthId, error: storableError(e) }));
  }
};

export const requestFetchAvailabilityExceptions =
  (fetchParams) => async (dispatch, getState, sdk) => {
    const { listingId, start, end } = fetchParams;
    // When using time-based process, you might want to deal with local dates using monthIdString
    const monthId = monthIdStringInUTC(start);

    dispatch(fetchAvailabilityExceptionsRequest({ ...fetchParams, monthId }));

    const startMonthId = monthIdStringInUTC(moment(start).clone().subtract(1, 'month').toDate());
    const startMonthStart = moment(start)
      .clone()
      .subtract(1, 'month')
      .startOf('month')
      .toISOString();
    const startMonthEnd = moment(start).clone().subtract(1, 'month').endOf('month').toISOString();
    const middleMonthId = monthIdStringInUTC(moment(start).clone().toDate());
    const middleMonthStart = moment(start).clone().startOf('month').toISOString();
    const middleMonthEnd = moment(start).clone().endOf('month').toISOString();
    const endMonthId = monthIdStringInUTC(moment(start).clone().add(1, 'month').toDate());
    const endMonthStart = moment(start).clone().add(1, 'month').startOf('month').toISOString();
    const endMonthEnd = moment(start).clone().add(1, 'month').endOf('month').toISOString();

    try {
      const [startMonth, middleMonth, endMonth] = await Promise.all([
        sdk.availabilityExceptions.query(
          { listingId, start: startMonthStart, end: startMonthEnd },
          { expand: true }
        ),
        sdk.availabilityExceptions.query(
          { listingId, start: middleMonthStart, end: middleMonthEnd },
          { expand: true }
        ),
        sdk.availabilityExceptions.query(
          { listingId, start: endMonthStart, end: endMonthEnd },
          { expand: true }
        ),
      ]);
      const allExceptions = [];
      let exceptions = denormalisedResponseEntities(startMonth).map((availabilityException) => ({
        availabilityException,
      }));
      allExceptions.push(...exceptions);
      dispatch(fetchAvailabilityExceptionsSuccess({ data: { monthId: startMonthId, exceptions } }));
      exceptions = denormalisedResponseEntities(middleMonth).map((availabilityException) => ({
        availabilityException,
      }));
      allExceptions.push(...exceptions);
      dispatch(
        fetchAvailabilityExceptionsSuccess({ data: { monthId: middleMonthId, exceptions } })
      );
      exceptions = denormalisedResponseEntities(endMonth).map((availabilityException) => ({
        availabilityException,
      }));
      allExceptions.push(...exceptions);
      dispatch(fetchAvailabilityExceptionsSuccess({ data: { monthId: endMonthId, exceptions } }));
      return allExceptions;
    } catch (e) {
      logError(e, 'fetch-availability-exceptions', { listingId, start, end });
      return dispatch(fetchAvailabilityExceptionsError({ monthId, error: storableError(e) }));
    }
  };

export const requestCreateAvailabilityException = (params) => (dispatch, getState, sdk) => {
  const { currentException, ...createParams } = params;
  dispatch(createAvailabilityExceptionRequest(createParams));
  return sdk.availabilityExceptions
    .create(createParams, { expand: true })
    .then((response) => {
      dispatch(
        createAvailabilityExceptionSuccess({
          data: {
            exception: {
              availabilityException: response.data.data,
            },
          },
        })
      );
      return response;
    })
    .catch((error) => {
      logError(error, 'create-availability-exception', { params });
      const availabilityException = currentException && currentException.availabilityException;
      return dispatch(
        createAvailabilityExceptionError({
          error: storableError(error),
          availabilityException,
        })
      );
    });
};

export const requestDeleteAvailabilityException = (params) => (dispatch, getState, sdk) => {
  const { currentException, ...deleteParams } = params;
  delete deleteParams.seats;

  dispatch(deleteAvailabilityExceptionRequest(params));

  return sdk.availabilityExceptions
    .delete(deleteParams, { expand: true })
    .then((response) => {
      console.log(response);
      dispatch(
        deleteAvailabilityExceptionSuccess({
          data: {
            exception: currentException,
          },
        })
      );
      return response;
    })
    .catch((error) => {
      logError(error, 'delete-availability-exception', { params });
      const availabilityException = currentException && currentException.availabilityException;
      return dispatch(
        deleteAvailabilityExceptionError({
          error: storableError(error),
          availabilityException,
        })
      );
    });
};

export const clearAvailabilityCalendar = () => async (dispatch) => {
  return dispatch(clearAvailabilityCalendarRequest());
};

export const updateListing = (listingId, values) => async (dispatch, getState, sdk) => {
  const updateValues = {
    id: listingId,
    ...values,
  };

  dispatch(updateOwnListingRequest());
  try {
    const response = await sdk.ownListings.update(updateValues, { expand: true });
    await dispatch(addOwnEntities(response));
    dispatch(updateOwnListingSuccess(response.data.data));
    return response;
  } catch (e) {
    logError(e, 'update-listing', { listingId, values });
    dispatch(updateOwnListingError(storableError(e)));
  }
};

export const loadData = (params, search) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  return queryOwnListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    'limit.images': 1,
  });
};
